import { AnalyticsApi } from './analyticsApi'
import { fetchArtistChats } from './chatData'
import { collection, query, where, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore'
import { db } from './firebase'

export class ArtistDashboardData {
  private analyticsApi: AnalyticsApi

  constructor() {
    this.analyticsApi = new AnalyticsApi()
  }

  async getArtistAnalytics(userId: string, days: number = 7) {
    // First get the artistId from the user document
    const artistId = await this.getArtistIdFromUser(userId)
    console.log(`[getArtistAnalytics] User ${userId} has artistId: ${artistId}`)
    
    if (!artistId) {
      console.log(`[getArtistAnalytics] User ${userId} is not an artist or missing artistId`)
      return {
        profileViews: 0,
        activeChats: 0,
        mostSavedFlash: null,
        lastUpdated: new Date()
      }
    }

    const [profileViews, artistChats, mostSavedFlash] = await Promise.all([
      this.analyticsApi.getArtistProfileViews(artistId, days),
      fetchArtistChats(),
      this.getMostSavedFlash(artistId)
    ])

    // Count active chats: either chat.status === 'active' OR there's a non-declined lead
    const activeChats = await this.countActiveChatsWithLeads(artistChats)

    return {
      profileViews: this.parseAnalyticsResponse(profileViews),
      activeChats,
      mostSavedFlash,
      lastUpdated: new Date()
    }
  }

  private async getArtistIdFromUser(userId: string): Promise<string | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId))
      if (!userDoc.exists()) {
        console.log(`[getArtistIdFromUser] User document not found for ${userId}`)
        return null
      }
      
      const userData = userDoc.data()
      console.log(`[getArtistIdFromUser] User data:`, {
        isArtist: userData.isArtist,
        artistId: userData.artistId
      })
      
      return userData.artistId || null
    } catch (error) {
      console.error('Error getting artist ID from user:', error)
      return null
    }
  }

  private async getMostSavedFlash(artistId: string) {
    try {
      console.log(`[getMostSavedFlash] Fetching flashes for artist: ${artistId}`)
      
      const flashesQuery = query(
        collection(db, 'flashes'),
        where('artistId', '==', artistId)
      )
      
      const querySnapshot = await getDocs(flashesQuery)
      
      console.log(`[getMostSavedFlash] Found ${querySnapshot.docs.length} flashes for artist`)
      
      if (querySnapshot.empty) {
        console.log(`[getMostSavedFlash] No flashes found for artist ${artistId}`)
        return null
      }

      // Calculate save count for each flash and find the highest
      let mostSaved = null
      let maxSaves = 0

      querySnapshot.docs.forEach((doc, index) => {
        const data = doc.data()
        const saveCount = data.saves?.length || 0
        
        console.log(`[getMostSavedFlash] Flash ${index + 1}:`, {
          id: doc.id,
          title: data.title || 'Untitled Flash',
          savesArray: data.saves || [],
          saveCount: saveCount,
          savesType: typeof data.saves,
          isArray: Array.isArray(data.saves),
          // Debug all available fields
          allFields: Object.keys(data),
          imageUrl: data.imageUrl,
          storagePath: data.storagePath,
          compressedImageUrl: data.compressedImageUrl
        })
        
        if (saveCount > maxSaves) {
          maxSaves = saveCount
          mostSaved = {
            id: doc.id,
            imageUrl: data.imageUrl || data.compressedImageUrl || data.storagePath || '',
            saves: saveCount
          }
          console.log(`[getMostSavedFlash] New most saved with ${saveCount} saves`)
        }
      })
      
      console.log(`[getMostSavedFlash] Final result:`, mostSaved)
      return mostSaved
    } catch (error) {
      console.error('Error fetching most saved flash:', error)
      return null
    }
  }

  private async countActiveChatsWithLeads(chats: any[]): Promise<number> {
    if (chats.length === 0) return 0

    try {
      // Get chat IDs to fetch corresponding leads
      const chatIds = chats.map(chat => chat.id)
      
      // Fetch leads for these chats
      const leadsQuery = query(
        collection(db, 'leads'),
        where('chatId', 'in', chatIds)
      )
      
      const leadsSnapshot = await getDocs(leadsQuery)
      const leads: Record<string, any> = {}
      
      leadsSnapshot.docs.forEach(doc => {
        const data = doc.data()
        leads[data.chatId] = {
          chatId: doc.id,
          type: data.type,
          userId: data.userId,
          artistId: data.artistId,
          flashId: data.flashId,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
          status: data.status,
          shortSummary: data.shortSummary,
          longSummary: data.longSummary,
          estimatedPrice: data.estimatedPrice
        }
      })

      // Count chats using the same logic as ChatList for artist view:
      // Only count chats that have a lead and the lead is not declined
      let activeCount = 0
      for (const chat of chats) {
        const lead = leads[chat.id]
        
        // For artists: only count chats that have a corresponding lead document
        if (!lead) continue
        
        // Hide declined leads
        if (lead.status === 'declined') continue
        
        activeCount++
      }

      console.log(`[countActiveChatsWithLeads] Found ${activeCount} active chats out of ${chats.length} total chats`)
      return activeCount
    } catch (error) {
      console.error('Error counting active chats with leads:', error)
      // Fallback to original logic if lead fetching fails
      return chats.filter(chat => chat.status === 'active').length
    }
  }

  private parseAnalyticsResponse(response: any) {
    return parseInt(response.rows?.[0]?.metricValues?.[0]?.value || '0')
  }
}